import React from 'react';
import styled from 'styled-components';
import { FieldPanelFullWidth, HeaderCaptionSemiBold } from 'styles/v2/Styles.styled';
import { ExpandableHelpHtml } from 'components/v2/help-boxes/ExpandableHelpHtml';
import TooltipErrorIcon from 'components/v2/icons/TooltipErrorIcon';
import TooltipHelpIcon from 'components/v2/icons/TooltipHelpIcon';
import { FieldPanelRecord } from 'types/dynamic-forms/records/FieldPanelRecord';
import DynamicContentRenderer from '../DynamicContentRenderer';
import useProperty from '../hooks/useProperty';
import usePropertyPath from '../hooks/usePropertyPath';
import useNestedErrors from '../hooks/useNestedErrors';

// When every child renders nothing (e.g. all fields are hidden in the current document state),
// the panel ends up with no DOM content. Collapse it so no empty box/background is shown.
const SelfHidingFieldPanel = styled(FieldPanelFullWidth)`
  &:empty {
    display: none;
  }
`;

const FieldPanelRenderer: React.FC<FieldPanelRecord> = ({
  children,
  panelHeader,
  tooltip,
  helpText,
  requiredIndicatorPropertyId,
}) => {
  return (
    <SelfHidingFieldPanel>
      {panelHeader && (
        <HeaderCaptionSemiBold>
          {panelHeader}
          {typeof tooltip === 'string' && tooltip && (
            <HeaderIconWrapper>
              <TooltipHelpIcon content={tooltip} />
            </HeaderIconWrapper>
          )}
          {requiredIndicatorPropertyId && <PanelRequiredIndicator propertyId={requiredIndicatorPropertyId} />}
        </HeaderCaptionSemiBold>
      )}
      {helpText && <ExpandableHelpHtml html={helpText} />}
      <DynamicContentRenderer schema={children} />
    </SelfHidingFieldPanel>
  );
};

// Shows the standard "Required" attention icon next to the panel header while the bound
// property has a validation error (e.g. an empty required list). Mirrors the compiler's
// section headers (see ContentDeclarationTab / BiogasDataGrid).
const PanelRequiredIndicator: React.FC<{ propertyId: string }> = ({ propertyId }) => {
  const path = usePropertyPath(propertyId);
  const { isVisible } = useProperty(path);
  const errorMessages = useNestedErrors(path);
  if (!isVisible || errorMessages.length === 0) {
    return null;
  }
  return (
    <HeaderIconWrapper>
      <TooltipErrorIcon content={errorMessages[0]} />
    </HeaderIconWrapper>
  );
};

// Spacing for an icon placed after the panel header text: the `tooltip` help icon
// (null or empty renders nothing) and the required indicator.
const HeaderIconWrapper = styled.span`
  margin-left: 0.25rem;
`;

export default FieldPanelRenderer;
